import { mapNewEvidenceBanner } from './intelligenceInsightsNewEvidence';
import {
    filterSupportedInsightsActions,
    firstSupportedInsightsAction,
} from './intelligenceInsightsActions';

function withSupportedActions(item, moduleKey) {
    if (!item) return item;
    const options = { moduleKey, context: { item } };
    const actions = filterSupportedInsightsActions(item.actions, options);
    const primaryAction =
        firstSupportedInsightsAction(
            [item.primaryAction, item.action, ...(item.actions || [])],
            options,
        ) || null;
    const action =
        firstSupportedInsightsAction([item.action, primaryAction, ...actions], options) ||
        primaryAction;
    return { ...item, actions, action, primaryAction };
}

function titleCaseToken(value) {
    if (!value) return '';
    return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

/** Narrative fields only — never stringify count/stats objects into JSX. */
function asDisplayText(value) {
    if (value == null || value === '') return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
}

function asDisplayTextList(value) {
    if (Array.isArray(value)) {
        return value.map(asDisplayText).filter(Boolean);
    }
    const text = asDisplayText(value);
    return text ? [text] : [];
}

/** Eight controlled scope areas for Intelligence Insights v2 evidence panels. */
export const INTELLIGENCE_INSIGHTS_SCOPE_AREAS = [
    'team',
    'strategy_roadmap',
    'technology_architecture',
    'scale_resilience',
    'service_management_devops',
    'compliance_cybersecurity',
    'business_systems_it',
    'it_budget',
];

const INTELLIGENCE_INSIGHTS_SCOPE_AREA_SET = new Set(INTELLIGENCE_INSIGHTS_SCOPE_AREAS);

const EVIDENCE_PANEL_NON_OBJECT_TYPES = new Set(['scope_area', 'overall_assessment']);

export function isIntelligenceInsightsScopeArea(value) {
    return INTELLIGENCE_INSIGHTS_SCOPE_AREA_SET.has(String(value || '').trim());
}

/**
 * Resolve scope_area for the universal evidence panel when no object target is available.
 * Ignores action target ids like overall_assessment → "overall".
 */
export function resolveEvidencePanelScopeArea({
    data = null,
    target = {},
    context = {},
    preferredKeys = [],
} = {}) {
    const fromContext = context.evidenceArea;
    if (isIntelligenceInsightsScopeArea(fromContext)) return fromContext;

    const targetType = String(target.object_type || target.type || '').toLowerCase();
    if (targetType === 'scope_area') {
        const area = target.scope_area || target.id;
        if (isIntelligenceInsightsScopeArea(area)) return area;
    }
    if (isIntelligenceInsightsScopeArea(target.scope_area)) return target.scope_area;

    for (const key of preferredKeys) {
        if (isIntelligenceInsightsScopeArea(key)) return key;
    }

    const scopes = data?.scopes || [];
    for (const key of preferredKeys) {
        if (key && scopes.some(s => s.key === key)) return key;
    }
    const ranked = [...scopes].sort((a, b) => {
        const order = { red: 0, amber: 1, green: 2, grey: 3 };
        return (order[a.rag] ?? 9) - (order[b.rag] ?? 9);
    });
    const firstScope = ranked[0]?.key;
    if (isIntelligenceInsightsScopeArea(firstScope)) return firstScope;

    return 'technology_architecture';
}

/** Canonical object target for object-mode evidence panel (finding, risk, evidence_matter, …). */
export function resolveEvidenceObjectTarget(target = {}, context = {}) {
    const rawType = target.object_type || target.type || context.objectType || null;
    const objectType = rawType ? String(rawType).trim().toLowerCase() : null;
    if (!objectType || EVIDENCE_PANEL_NON_OBJECT_TYPES.has(objectType)) {
        return { objectType: null, objectId: null };
    }
    const objectId =
        target.object_id ||
        context.objectId ||
        context.primaryOriginId ||
        (target.id ? String(target.id) : null);
    return {
        objectType,
        objectId: objectId || null,
    };
}

function normalizeWorkflowFields(m) {
    const raw = String(m?.workflow_status || m?.status || 'open')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
    const workflowKey =
        !raw || raw === '—' || raw === '-' || raw === 'unknown' ? 'open' : raw;
    return {
        workflow: titleCaseToken(workflowKey),
        workflowKey,
    };
}

function formatMetaLine(payload) {
    const analysis = payload?.analysis || {};
    const context = payload?.context || {};
    const label = context.label || 'Entire Project';
    if (!analysis.version) {
        return analysis.status === 'not_generated' ? `${label} · Not yet generated` : label;
    }
    let generated = '';
    if (analysis.generated_at) {
        const d = new Date(analysis.generated_at);
        if (!Number.isNaN(d.getTime())) {
            generated = `Generated ${d.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            })}`;
        }
    }
    return [label, `Version ${analysis.version}`, generated].filter(Boolean).join(' · ');
}

function confidenceLine(trust = {}, overall = {}) {
    const conf = titleCaseToken(overall.confidence || trust.confidence);
    const cov = titleCaseToken(overall.evidence_coverage || trust.evidence_coverage);
    if (conf && cov) return `${conf} confidence · ${cov} evidence`;
    if (conf) return `${conf} confidence`;
    return null;
}

function polarityToTone(polarity) {
    if (polarity === 'positive') return 'positive';
    if (polarity === 'negative') return 'warning';
    return 'info';
}

function emptyVm(moduleKey, title, subtitle, payload) {
    const empty = payload?.data?.empty_state || null;
    const status = payload?.analysis?.status || 'not_generated';
    const isFailed = status === 'failed' || empty?.type === 'failed';
    const primary = empty?.primary_action || empty?.primaryAction || null;
    return {
        moduleKey,
        title,
        subtitle,
        metaLine: formatMetaLine(payload),
        emptyState: {
            type: empty?.type || (isFailed ? 'failed' : 'not_generated'),
            title:
                empty?.title ||
                (isFailed ? `${title} could not be generated` : `${title} not yet generated`),
            message:
                empty?.message ||
                (isFailed
                    ? 'Generation failed. Retry to attempt regeneration using the same source snapshot.'
                    : 'Generate an analysis version from Overview to populate this module.'),
            meta: empty?.meta || null,
            primaryAction: primary
                ? {
                      key: primary.key || primary.action_type,
                      label: primary.label,
                  }
                : {
                      key: isFailed ? 'retry' : 'generate_analysis',
                      label: isFailed ? `Retry ${title} →` : `Generate ${title} →`,
                  },
        },
        isEmpty: true,
        status,
    };
}

export function managementSummaryPayloadToViewModel(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload.data || {};
    if (data.empty_state && !data.overall_assessment && !data.priority_takeaways) {
        return emptyVm(
            'management_summary',
            'Management Summary',
            'Primary executive technology assessment and eight-area evaluation.',
            payload,
        );
    }
    const overall = data.overall_assessment || {};
    const detail = overall.detail || {};
    const company =
        typeof data.company_overview === 'string'
            ? data.company_overview
            : data.company_overview?.text || '';
    const matrix = data.scope_assessment_matrix || data.scope_assessments || [];
    const banner = data.material_unresolved_banner || {};
    const profile = data.assessment_profile || { green: 0, amber: 0, red: 0, grey: 0 };
    const mapAction = action =>
        action
            ? {
                  key: action.key || action.action_type,
                  label: action.label,
                  target: action.target || null,
              }
            : null;

    return {
        moduleKey: 'management_summary',
        title: 'Management Summary',
        subtitle: 'Primary executive technology assessment and eight-area evaluation.',
        metaLine: formatMetaLine(payload),
        isEmpty: false,
        status: payload.analysis?.status,
        companyOverview: company,
        overall: {
            rag: String(overall.rag || 'grey').toLowerCase(),
            ragLabel: titleCaseToken(overall.rag || 'grey'),
            headline: asDisplayText(overall.headline) || asDisplayText(overall.conclusion),
            summary: asDisplayText(overall.summary) || asDisplayText(overall.rationale),
            confidence: confidenceLine(payload.trust, overall),
            evidenceReferenceIds: overall.evidence_reference_ids || [],
            actions: filterSupportedInsightsActions(
                (overall.actions || []).map(mapAction).filter(Boolean),
                { moduleKey: 'management_summary' },
            ),
        },
        overallDetail: {
            supportingStrengths: detail.supporting_strengths || [],
            constrainingFactors: detail.constraining_factors || [],
            crossCuttingImplications: detail.cross_cutting_implications || [],
            assessmentLimitations: detail.assessment_limitations || [],
        },
        takeaways: (data.priority_takeaways || []).map((t, idx) => {
            const action = t.primary_action || (t.actions || [])[0] || null;
            const scopes = t.scope_areas || [];
            const origins = t.origin_object_ids || [];
            const evidenceAction =
                (t.actions || []).find(
                    a =>
                        (a.key || a.action_type) === 'view_evidence' ||
                        a.action_type === 'open_evidence_panel',
                ) || null;
            return withSupportedActions(
                {
                    id: t.takeaway_id || `t${idx + 1}`,
                    title: t.title || '',
                    body: t.body || '',
                    tone: polarityToTone(t.polarity),
                    materiality: t.materiality || 'Notable',
                    classification: t.classification || null,
                    scopes: scopes.map(s => String(s).replace(/_/g, ' ').toLowerCase()),
                    scopeKeys: scopes,
                    originObjectIds: origins,
                    primaryOriginId: origins[0] || null,
                    evidenceReferenceIds: t.evidence_reference_ids || [],
                    action: mapAction(action),
                    evidenceAction: mapAction(evidenceAction || action),
                    actions: (t.actions || []).map(mapAction).filter(Boolean),
                },
                'management_summary',
            );
        }),
        profile,
        profileNote: data.profile_note || null,
        scopes: matrix.map(scope => {
            const conf = titleCaseToken(scope.confidence);
            const cov = titleCaseToken(scope.evidence_coverage);
            const kfTarget = scope.key_findings_target || {};
            return {
                key: scope.scope_area,
                label: scope.display_label || titleCaseToken(scope.scope_area),
                rag: String(scope.rag || 'grey').toLowerCase(),
                text: scope.conclusion || '',
                unresolved: Number(
                    scope.material_unresolved_count ?? scope.unresolved_count ?? 0,
                ),
                confidence: conf && cov ? `${conf} confidence · ${cov} evidence` : conf || null,
                evidenceReferenceIds: scope.evidence_reference_ids || [],
                keyFindingsScope: kfTarget.scope_area || scope.scope_area,
            };
        }),
        footerBanner: banner.visible
            ? {
                  title: banner.title || banner.message || '',
                  affected:
                      banner.affected ||
                      banner.message ||
                      (banner.count
                          ? `${banner.count} material evidence matter(s) limit confidence.`
                          : ''),
                  actionLabel: banner.action?.label || 'View in Evidence & Follow-Ups →',
                  actionKey:
                      banner.action?.target ||
                      banner.target_module ||
                      'evidence_follow_ups',
              }
            : null,
    };
}

function mapFindingItem(item, i, fallbackPrefix) {
    const mapAction = action =>
        action
            ? {
                  key: action.key || action.action_type,
                  label: action.label,
                  target: action.target || null,
              }
            : null;
    const type = item.type || 'finding';
    const typeLabel =
        item.type_label ||
        (type === 'evidence_matter'
            ? 'Evidence gap'
            : type === 'contradiction'
              ? 'Contradiction'
              : null);
    const priorityKey = String(item.priority || '').toLowerCase();
    const priorityLabel =
        item.priority_label ||
        (priorityKey === 'high' || priorityKey.includes('high') || priorityKey.includes('material')
            ? 'High priority'
            : priorityKey === 'medium' || priorityKey.includes('medium') || priorityKey.includes('notable')
              ? 'Medium priority'
              : priorityKey === 'low' || priorityKey.includes('low')
                ? 'Low priority'
                : type === 'evidence_matter' || type === 'contradiction'
                  ? 'High priority'
                  : null);
    const materialityRaw = String(item.materiality || '').trim();
    const materiality =
        type === 'evidence_matter' || type === 'contradiction'
            ? materialityRaw && !/^notable$/i.test(materialityRaw)
                ? materialityRaw
                : 'Material'
            : materialityRaw || 'Notable';
    const mapped = {
        id: item.finding_id || item.id || item.evidence_matter_id || item.contradiction_id || `${fallbackPrefix}${i}`,
        findingId: item.finding_id || null,
        type,
        typeLabel,
        title: asDisplayText(item.title),
        body: asDisplayText(item.summary) || asDisplayText(item.body),
        whyItMatters: item.why_it_matters || '',
        impactNote:
            item.impact_note ||
            (type === 'evidence_matter' || type === 'contradiction'
                ? 'May reduce confidence'
                : null),
        materiality,
        priority: priorityKey || null,
        priorityLabel,
        confidence: item.confidence || null,
        evidenceCoverage: item.evidence_coverage || null,
        relatedRiskIds: item.related_risk_ids || [],
        contradictionIds: item.contradiction_ids || [],
        evidenceReferenceIds: item.evidence_reference_ids || [],
        detail: {
            fullAssessment:
                asDisplayText(item.detail?.full_assessment) ||
                asDisplayText(item.summary) ||
                asDisplayText(item.body),
            recommendedResponse: asDisplayText(item.detail?.recommended_response) || null,
        },
        actions: (item.actions || []).map(mapAction).filter(Boolean),
        primaryAction: mapAction(item.primary_action || (item.actions || [])[0] || null),
    };
    return withSupportedActions(mapped, 'key_findings');
}

export function keyFindingsPayloadToViewModel(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload.data || {};
    if (data.empty_state && !(data.areas || []).length && !data.active_scope) {
        return emptyVm(
            'key_findings',
            'Key Findings',
            'Detailed technology assessment across eight controlled areas.',
            payload,
        );
    }
    const mapAction = action =>
        action
            ? {
                  key: action.key || action.action_type,
                  label: action.label,
                  target: action.target || null,
              }
            : null;

    const areas = (data.areas || data.scope_selector || []).map(area => {
        const items = area.items || area.tabs || {};
        return {
            key: area.scope_area,
            label: area.display_label || titleCaseToken(area.scope_area),
            rag: String(area.rag || 'grey').toLowerCase(),
            positives: Number(area.positives_count ?? items?.positives?.length ?? 0),
            concerns: Number(area.concerns_count ?? items?.concerns?.length ?? 0),
            unresolved: Number(area.unresolved_count ?? items?.unresolved?.length ?? 0),
            summary: asDisplayText(area.summary) || asDisplayText(area.conclusion),
            rationale: area.rationale || '',
            confidence: area.confidence || null,
            evidenceCoverage: area.evidence_coverage || null,
            evidenceReferenceIds: area.evidence_reference_ids || [],
            actions: (area.actions || []).map(mapAction).filter(Boolean),
            items: {
                positives: (items.positives || []).map((item, i) => mapFindingItem(item, i, 'p')),
                concerns: (items.concerns || []).map((item, i) => mapFindingItem(item, i, 'c')),
                unresolved: (items.unresolved || []).map((item, i) => mapFindingItem(item, i, 'u')),
            },
        };
    });
    const reporting = data.reporting || {};
    const filters = data.filters || {};
    return {
        moduleKey: 'key_findings',
        title: 'Key Findings',
        subtitle: 'Detailed technology assessment across eight controlled areas.',
        metaLine: formatMetaLine(payload),
        isEmpty: areas.length === 0,
        emptyState: areas.length === 0 ? data.empty_state : null,
        status: payload.analysis?.status,
        warnings: payload.warnings || [],
        areas,
        activeScopeKey: data.active_scope?.scope_area || areas[0]?.key || null,
        filters: {
            materiality: filters.materiality || 'all',
            confidence: filters.confidence || 'all',
            unresolvedOnly: Boolean(filters.unresolved_only),
            options: filters.options || {
                materiality: ['all', 'material', 'notable'],
                confidence: ['all', 'high', 'medium', 'low'],
            },
        },
        reporting: {
            createReportDraftAvailable: reporting.create_report_draft_available !== false,
            exportScopeAvailable: reporting.export_scope_available !== false,
        },
    };
}

const SWOT_META = {
    strengths: {
        label: 'Strengths',
        tone: 'green',
        definition:
            'Internal technology capabilities that create competitive or operational advantage.',
    },
    weaknesses: {
        label: 'Weaknesses',
        tone: 'amber',
        definition:
            'Internal technology limitations or gaps that present risk or constrain value.',
    },
    opportunities: {
        label: 'Opportunities',
        tone: 'blue',
        definition:
            'External conditions or strategic positions that could create value post-acquisition.',
    },
    threats: {
        label: 'Threats',
        tone: 'red',
        definition: 'External conditions or risks that could constrain or damage value.',
    },
};

function mapSwotAction(action) {
    if (!action) return null;
    return {
        key: action.key || action.action_type,
        label: action.label,
        target: action.target || null,
    };
}

function mapSwotItem(item, index, quadrantKey) {
    const detail = item.detail || {};
    const mapped = {
        id: item.swot_item_id || item.id || `${quadrantKey[0]}${index + 1}`,
        quadrantKey,
        quadrantLabel: SWOT_META[quadrantKey]?.label || titleCaseToken(quadrantKey),
        materiality: item.materiality || 'Notable',
        evidence: String(item.evidence_indicator || item.evidence || 'moderate').toLowerCase(),
        title: asDisplayText(item.title),
        body:
            asDisplayText(item.assessment) ||
            asDisplayText(item.body) ||
            asDisplayText(item.summary),
        relatedFindingIds: item.related_finding_ids || [],
        relatedRiskIds: item.related_risk_ids || [],
        relatedThemeIds: item.related_theme_ids || [],
        relatedEvidenceMatterIds: item.related_evidence_matter_ids || [],
        evidenceReferenceIds: item.evidence_reference_ids || [],
        detail: {
            whyItMatters: detail.why_it_matters || '',
            strategicImplication: detail.strategic_implication || '',
            scopeAreas: detail.scope_areas || [],
            confidence: detail.confidence || null,
            evidenceCoverage: detail.evidence_coverage || null,
            assessmentLimitations: detail.assessment_limitations || [],
            fullAssessment:
                asDisplayText(detail.full_assessment) ||
                asDisplayText(item.assessment) ||
                asDisplayText(item.body),
            recommendedResponse: asDisplayText(detail.recommended_response) || null,
            sourcesReviewed: Number(
                detail.sources_reviewed ?? (item.evidence_reference_ids || []).length,
            ),
        },
        actions: (item.actions || []).map(mapSwotAction).filter(Boolean),
        action: mapSwotAction(item.primary_action) || null,
        primaryAction: mapSwotAction(item.primary_action) || null,
    };
    const withActions = withSupportedActions(mapped, 'swot_analysis');
    const secondary =
        withActions.actions.find(
            a =>
                a.key &&
                !['view_details', 'view_evidence', 'open_evidence_panel'].includes(a.key),
        ) || null;
    return {
        ...withActions,
        action: secondary || withActions.primaryAction || null,
    };
}

export function swotPayloadToViewModel(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload.data || {};
    const quads = data.quadrants || {};
    const hasItems = Object.values(quads).some(
        pack => Array.isArray(pack?.items) && pack.items.length > 0,
    );
    if (data.empty_state && !hasItems && !Object.keys(quads).length) {
        return emptyVm(
            'swot_analysis',
            'SWOT Analysis',
            'Evidence-backed strategic strengths, weaknesses, opportunities and threats.',
            payload,
        );
    }
    const filters = data.filters || {};
    const quadrants = Object.keys(SWOT_META).map(key => {
        const meta = SWOT_META[key];
        const pack = quads[key] || {};
        const items = (pack.items || []).map((item, i) => mapSwotItem(item, i, key));
        return {
            key,
            label: pack.label || meta.label,
            tone: meta.tone,
            definition: pack.definition || meta.definition,
            itemCount: Number(pack.item_count ?? items.length),
            items,
        };
    });
    const isEmpty = quadrants.every(q => !q.items.length) && Boolean(data.empty_state);
    return {
        moduleKey: 'swot_analysis',
        title: 'SWOT Analysis',
        subtitle: 'Evidence-backed strategic strengths, weaknesses, opportunities and threats.',
        metaLine: formatMetaLine(payload),
        isEmpty,
        emptyState: isEmpty ? data.empty_state : null,
        status: payload.analysis?.status,
        warnings: payload.warnings || [],
        filters: {
            materiality: filters.materiality || 'all',
            scopeArea: filters.scope_area || 'all',
            confidence: filters.confidence || 'all',
            scopeAreaLabels: filters.scope_area_labels || {},
            options: filters.options || {
                materiality: ['all', 'material', 'notable'],
                scope_area: ['all'],
                confidence: ['all', 'high', 'medium', 'low'],
            },
        },
        quadrants,
    };
}

function mapAction(action) {
    return action
        ? {
              key: action.key || action.action_type,
              label: action.label,
              target: action.target || null,
          }
        : null;
}

function postureToneFromValue(value, explicitTone) {
    if (explicitTone) return String(explicitTone).toLowerCase();
    const v = String(value || '').toLowerCase();
    if (v === 'supportive' || v === 'proceed') return 'green';
    if (v === 'adverse' || v === 'do_not_proceed') return 'red';
    if (v === 'insufficient_evidence' || v === 'insufficient') return 'grey';
    return 'amber'; // conditional | cautious
}

export function investmentOpinionPayloadToViewModel(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload.data || {};
    if (
        data.empty_state &&
        !data.deal_posture &&
        !data.headline &&
        !(data.conditions || []).length
    ) {
        return emptyVm(
            'investment_opinion',
            'Investment Opinion',
            'Technology-led interpretation of the transaction and investment case.',
            payload,
        );
    }
    const posture = data.deal_posture || {};
    const value = String(posture.value || posture.label || 'conditional')
        .toLowerCase()
        .replace(/\s+/g, '_');
    const tone = postureToneFromValue(value, posture.tone);
    const conf = confidenceLine(payload.trust, {
        confidence: posture.confidence || data.confidence,
        evidence_coverage: posture.evidence_coverage || data.evidence_coverage,
    });
    const mapFactor = (item, i, prefix) => {
        const body = asDisplayText(item.body) || asDisplayText(item.summary);
        return withSupportedActions(
            {
                id: item.id || item.factor_id || `${prefix}${i + 1}`,
                title: asDisplayText(item.title),
                body,
                materiality: item.materiality || (prefix === 'sf' ? 'Notable' : 'Material'),
                consequence: item.consequence || item.potential_consequence || null,
                relevance: item.relevance || item.investment_relevance || null,
                evidenceReferenceIds: item.evidence_reference_ids || [],
                actions: (item.actions || []).map(mapAction).filter(Boolean),
            },
            'investment_opinion',
        );
    };
    const balance = data.balance_view || {};
    const supporting =
        balance.deal_supporting_factors ||
        data.deal_supporting_factors ||
        data.supporting_factors ||
        data.thesis_items ||
        [];
    const concerns =
        balance.material_concerns || data.material_concerns || data.concerns || [];
    const thesisFallback = Array.isArray(data.thesis)
        ? data.thesis
        : data.thesis
          ? [data.thesis]
          : [];
    const investmentView = data.investment_view || {};
    const viewRows = (investmentView.rows || investmentView.checklist || []).map((row, i) => ({
        id: row.id || `iv${i + 1}`,
        label: row.label || row.key || '',
        value: asDisplayText(row.value) || asDisplayText(row.display),
        tone: row.tone || (String(row.value).toLowerCase() === 'yes' ? 'positive' : 'neutral'),
    }));
    const banner =
        data.limitations_banner ||
        data.evidence_limitations_banner ||
        data.footer_banner ||
        {};
    const rationale = data.rationale_detail || posture.rationale || data.rationale || {};
    const applicability = data.applicability || { enabled: true };
    return {
        moduleKey: 'investment_opinion',
        title: 'Investment Opinion',
        subtitle: 'Technology-led interpretation of the transaction and investment case.',
        metaLine: formatMetaLine(payload),
        isEmpty: false,
        applicability,
        status: payload.analysis?.status,
        disclaimer:
            data.disclaimer ||
            'Technology-led AI assessment. Not an investment recommendation.',
        posture: {
            label: posture.label || titleCaseToken(value),
            value,
            tone,
            headline: asDisplayText(posture.headline) || asDisplayText(data.headline),
            summary:
                asDisplayText(posture.summary) ||
                asDisplayText(posture.body) ||
                asDisplayText(data.summary),
            confidence: conf,
            actions: filterSupportedInsightsActions(
                (posture.actions || []).map(mapAction).filter(Boolean),
                { moduleKey: 'investment_opinion' },
            ),
            rationale: {
                criticalDependencies: rationale.critical_dependencies || [],
                assumptions: rationale.assumptions || [],
                implications: rationale.potential_transaction_implications || rationale.implications || [],
                alternativeScenarios: {
                    supportive: rationale.alternative_posture_scenarios?.supportive || [],
                    cautious: rationale.alternative_posture_scenarios?.cautious || [],
                },
                valueCreationPriorities: rationale.value_creation_priorities || [],
                supportingObjects: rationale.supporting_objects || [],
                constrainingObjects: rationale.constraining_objects || [],
                evidenceCount: Number(rationale.evidence_count || 0),
            },
        },
        supportingFactors:
            supporting.length > 0
                ? supporting.map((item, i) => mapFactor(item, i, 'sf'))
                : thesisFallback.map((text, i) => ({
                      id: `sf${i + 1}`,
                      title: typeof text === 'string' ? text : text.title || '',
                      body: typeof text === 'string' ? '' : text.body || '',
                      materiality: 'Notable',
                      consequence: null,
                      relevance: null,
                      evidenceReferenceIds: [],
                      actions: [],
                  })),
        materialConcerns: concerns.map((item, i) => mapFactor(item, i, 'mc')),
        investmentView: {
            summary:
                asDisplayText(investmentView.summary) ||
                asDisplayText(investmentView.narrative),
            rows: viewRows,
        },
        conditions: (data.conditions_to_resolve || data.conditions || []).map((c, i) =>
            withSupportedActions(
                {
                    id: c.id || c.condition_id || `io${i + 1}`,
                    title: c.title || '',
                    body: c.body || c.description || '',
                    priority: String(c.priority || 'high').toLowerCase(),
                    affectedPosture: c.affected_posture || null,
                    recommendedDestination: c.recommended_destination || null,
                    objectIds: c.object_ids || c.related_object_ids || [],
                    evidenceReferenceIds: c.evidence_reference_ids || [],
                    actions: (c.actions || []).map(mapAction).filter(Boolean),
                    action:
                        mapAction(c.primary_action || (c.actions || [])[0]) || {
                            key: 'add_to_raid',
                            label: 'Add to RAID',
                        },
                },
                'investment_opinion',
            ),
        ),
        confidence: conf,
        footerBanner: banner.visible !== false && (banner.title || banner.message || banner.count)
            ? {
                  title:
                      banner.title ||
                      banner.message ||
                      `${banner.count} evidence limitations affect the completeness of this assessment.`,
                  actionLabel: banner.action?.label || 'View in Evidence & Follow-Ups →',
                  actionKey:
                      banner.action?.target ||
                      banner.target_module ||
                      'evidence_follow_ups',
              }
            : null,
    };
}

function SCOPE_LABEL(area) {
    const map = {
        team: 'Team',
        strategy_roadmap: 'Strategy & Roadmap',
        technology_architecture: 'Technology & Architecture',
        scale_resilience: 'Scale & Resilience',
        service_management_devops: 'Service Management & DevOps',
        compliance_cybersecurity: 'Cybersecurity & Compliance',
        cybersecurity_compliance: 'Cybersecurity & Compliance',
        business_systems_it: 'Business Systems & IT',
        it_budget: 'IT Budget & Operations',
        it_budget_operations: 'IT Budget & Operations',
        vendor_dependencies: 'Vendor & Dependencies',
        data_ai: 'Data & AI',
    };
    return map[area] || titleCaseToken(area);
}

export function risksSignalsPayloadToViewModel(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload.data || {};
    if (data.empty_state && !(data.risk_register || data.risks || []).length) {
        return emptyVm(
            'risks_signals',
            'Risks & Signals',
            'Material technology risks, potential impacts and warning indicators.',
            payload,
        );
    }
    const risks = (data.risk_register || data.risks || []).map((r, i) => {
        const detail = r.detail || {};
        return withSupportedActions(
            {
                id: r.risk_id || r.id || `r${i + 1}`,
                severity: String(r.severity || 'medium').toLowerCase(),
                title: asDisplayText(r.title),
                body:
                    asDisplayText(r.body) ||
                    asDisplayText(r.summary) ||
                    asDisplayText(detail.description),
                scopes: (r.scope_areas || []).map(s => SCOPE_LABEL(s)),
                scopeKeys: r.scope_areas || [],
                confidence: titleCaseToken(r.confidence) || 'Medium',
                status: titleCaseToken(r.status || 'open'),
                statusKey: String(r.status || 'open').toLowerCase().replace(/\s+/g, '_'),
                action:
                    mapAction(r.primary_action) ||
                    mapAction((r.actions || [])[0]) || {
                        key: 'add_to_raid',
                        label: 'Add to RAID',
                    },
                actions: (r.actions || []).map(mapAction).filter(Boolean),
                evidenceReferenceIds: r.evidence_reference_ids || [],
                detail: {
                    whyRisk:
                        asDisplayText(detail.why_it_is_a_risk) ||
                        asDisplayText(detail.why_risk),
                    potentialImpact: asDisplayTextList(detail.potential_impact),
                    likelihood: asDisplayText(detail.likelihood) || null,
                    earlyWarningSignals: asDisplayTextList(detail.early_warning_signals),
                    recommendedResponse: asDisplayText(detail.recommended_response),
                relatedFindings: (detail.related_findings || []).map(item => {
                    if (item && typeof item === 'object') {
                        return {
                            id: item.id || item.finding_id || '',
                            label: item.label || `→ ${item.id || item.finding_id || ''} in Key Findings`,
                            moduleKey: item.module_key || 'key_findings',
                        };
                    }
                    return {
                        id: String(item),
                        label: `→ ${item} in Key Findings`,
                        moduleKey: 'key_findings',
                    };
                }),
                relatedEvidenceMatters: (detail.related_evidence_matters || []).map(item => {
                    if (item && typeof item === 'object') {
                        return {
                            id: item.id || item.evidence_matter_id || '',
                            label:
                                item.label ||
                                `→ ${item.id || item.evidence_matter_id || ''} in Evidence & Follow-Ups`,
                            moduleKey: item.module_key || 'evidence_follow_ups',
                        };
                    }
                    return {
                        id: String(item),
                        label: `→ ${item} in Evidence & Follow-Ups`,
                        moduleKey: 'evidence_follow_ups',
                    };
                }),
                evidenceCoverage: titleCaseToken(detail.evidence_coverage || r.evidence_coverage),
                evidenceCount:
                    detail.evidence_count ??
                    (Array.isArray(r.evidence_reference_ids) ? r.evidence_reference_ids.length : 0),
                raidRelationship: {
                    linked: Boolean(
                        detail.raid_relationship?.linked && detail.raid_relationship?.raid_item_id,
                    ),
                    raidItemId: detail.raid_relationship?.raid_item_id || null,
                    workflowStatus: detail.raid_relationship?.workflow_status || null,
                },
            },
            },
            'risks_signals',
        );
    });
    const signalRows = Array.isArray(data.signals) ? data.signals : [];
    const signals = signalRows.map((s, i) => ({
        id: s.id || `sig${i + 1}`,
        label: asDisplayText(s.label),
        tone: s.tone || 'warning',
    }));
    const summary = data.summary || {};
    const profile = data.risk_profile || {};
    const dist = data.severity_distribution || summary.distribution || profile || {};
    const critical = Number(
        dist.critical ?? profile.critical ?? risks.filter(r => r.severity === 'critical').length,
    );
    const high = Number(
        dist.high ?? profile.high ?? risks.filter(r => r.severity === 'high').length,
    );
    const medium = Number(
        dist.medium ?? profile.medium ?? risks.filter(r => r.severity === 'medium').length,
    );
    const low = Number(dist.low ?? profile.low ?? risks.filter(r => r.severity === 'low').length);
    const attentionCount = Number(
        profile.immediate_attention_count ??
            summary.immediate_attention_count ??
            summary.attention_count ??
            summary.require_attention ??
            critical + Math.min(high, 2),
    );
    const filters = data.filters || {};
    const scopeOptionKeys =
        filters.options?.scope_area || filters.options?.scope || ['all'];
    const scopeOptions = scopeOptionKeys.map(opt =>
        opt === 'all' ? 'all' : SCOPE_LABEL(opt),
    );
    return {
        moduleKey: 'risks_signals',
        title: 'Risks & Signals',
        subtitle: 'Material technology risks, potential impacts and warning indicators.',
        metaLine: formatMetaLine(payload),
        isEmpty: false,
        emptyRegister: Boolean(data.empty_register) || risks.length === 0,
        emptyRegisterMessage:
            data.empty_register_message ||
            (risks.length === 0
                ? 'No material canonical risks were identified in this analysis version.'
                : null),
        status: payload.analysis?.status,
        attentionBanner:
            attentionCount > 0
                ? `${attentionCount} risk${attentionCount === 1 ? '' : 's'} require immediate attention`
                : null,
        distribution: { critical, high, medium, low },
        filters: {
            severity: filters.severity || 'all',
            scope: filters.scope_area || filters.scope || 'all',
            status: filters.status || 'all',
            confidence: filters.confidence || 'all',
            options: {
                severity: filters.options?.severity || ['all', 'critical', 'high', 'medium', 'low'],
                scope: scopeOptions.length > 1 ? scopeOptions : ['all'],
                status: filters.options?.status || ['all', 'open', 'in_progress', 'mitigated'],
                confidence: filters.options?.confidence || ['all', 'high', 'medium', 'low'],
            },
        },
        summary: {
            material: Number(summary.material || critical + high),
            watch: Number(summary.watch || medium + low),
            signals: Number(summary.signals || signals.length),
        },
        risks,
        signals,
    };
}

export function evidenceFollowUpsPayloadToViewModel(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload.data || {};
    if (
        data.empty_state &&
        !(data.priority_follow_up_queue || data.matters || data.follow_ups || []).length &&
        !(data.coverage_matrix || []).length
    ) {
        return emptyVm(
            'evidence_follow_ups',
            'Evidence & Follow-Ups',
            'Evidence coverage, reliability and required follow-up actions.',
            payload,
        );
    }
    const position = data.overall_evidence_position || data.overall_position || {};
    const summary = data.summary || {};
    const followUps = (data.priority_follow_up_queue || data.follow_ups || data.matters || []).map(
        (m, i) => {
            const detail = m.detail || {};
            return withSupportedActions(
                {
                    id: m.evidence_matter_id || m.id || `em${i + 1}`,
                    title: asDisplayText(m.title),
                    body: asDisplayText(m.body) || asDisplayText(m.summary),
                    type: m.type_label || m.type || 'Evidence gap',
                    typeKey: String(m.type || 'evidence_gap').toLowerCase(),
                    scopes: (m.scope_areas || []).map(s => SCOPE_LABEL(s)),
                    scopeKeys: m.scope_areas || [],
                    priority: titleCaseToken(m.priority || 'high'),
                    priorityKey: String(m.priority || 'high').toLowerCase(),
                    impact: m.impact_label || m.impact || '',
                    ...normalizeWorkflowFields(m),
                    materiality: m.materiality || 'Notable',
                    status: m.status || 'open',
                    action:
                        mapAction(m.primary_action || (m.actions || [])[0]) || {
                            key: 'create_irl',
                            label: 'Create IRL',
                        },
                    actions: (m.actions || []).map(mapAction).filter(Boolean),
                    detail: {
                        missingOrUnresolved: detail.missing_or_unresolved || '',
                        whyItMatters: detail.why_it_matters || '',
                        affectedAssessments: (detail.affected_assessments || m.scope_areas || []).map(
                            s => (typeof s === 'string' ? SCOPE_LABEL(s) : s),
                        ),
                        ragImpact: detail.rag_impact || null,
                        confidenceImpact: detail.confidence_impact || null,
                        coverageImpact: detail.coverage_impact || null,
                        recommendedRequest: detail.recommended_request || '',
                        recommendedDestination: detail.recommended_destination || null,
                        existingWorkflowRecords: detail.existing_workflow_records || [],
                        assessmentImpact: m.assessment_impact || '',
                    },
                },
                'evidence_follow_ups',
            );
        },
    );
    const matrix = (data.coverage_matrix || data.assessment_coverage || []).map((row, i) => {
        const detail = row.detail || {};
        return {
            id: row.scope_area || row.id || `cov${i + 1}`,
            key: row.scope_area || row.key,
            label: row.display_label || SCOPE_LABEL(row.scope_area) || row.label || '',
            coverage: titleCaseToken(row.coverage || row.evidence_coverage || 'partial'),
            coverageKey: String(row.coverage || row.evidence_coverage || 'partial').toLowerCase(),
            confidence: titleCaseToken(row.confidence || 'medium'),
            openMatters: Number(row.open_matters ?? row.open ?? 0),
            primaryLimitation: row.primary_limitation || row.limitation || '—',
            detail: {
                wellSupported: detail.well_supported_topics || [],
                weaklySupported: detail.weakly_supported_topics || [],
                assessmentImpact: detail.assessment_impact || '',
                evidenceQuality: detail.evidence_quality || [],
                sourceMix: detail.source_mix || null,
                openMatters: detail.open_evidence_matters || [],
            },
        };
    });
    const filters = data.filters || {};
    const coverageLabel =
        titleCaseToken(position.coverage || data.evidence_coverage || summary.coverage) ||
        'Partial';
    // API may put count stats on overall_evidence_position.summary or data.summary.
    const positionSummaryStats =
        position.summary && typeof position.summary === 'object' && !Array.isArray(position.summary)
            ? position.summary
            : {};
    const summaryStats = {
        ...summary,
        ...positionSummaryStats,
    };
    const positionNarrative = [position.conclusion, position.narrative, position.text, position.assessment]
        .concat(typeof position.summary === 'string' ? [position.summary] : [])
        .find(v => typeof v === 'string' && v.trim());
    const readiness = data.source_readiness || {};
    const readinessTotals = readiness.totals || {};
    const failedSource = (readiness.unavailable_sources || []).find(
        s => String(s?.reason || '').toLowerCase() === 'failed',
    );
    const scopeOptionKeys =
        filters.options?.scope_area || filters.options?.scope || ['all'];
    return {
        moduleKey: 'evidence_follow_ups',
        title: 'Evidence & Follow-Ups',
        subtitle: 'Evidence coverage, reliability and required follow-up actions.',
        metaLine: formatMetaLine(payload),
        isEmpty: false,
        status: payload.analysis?.status,
        confidence: confidenceLine(payload.trust, {
            confidence: data.confidence || position.confidence,
            evidence_coverage: data.evidence_coverage || position.coverage,
        }),
        overallPosition: {
            coverage: coverageLabel,
            coverageKey: String(position.coverage || data.evidence_coverage || 'partial').toLowerCase(),
            summary:
                positionNarrative ||
                'Evidence coverage is sufficient for an overall assessment but constrained in selected areas.',
            limitedAreas: position.limited_areas || position.limited_coverage_areas || [],
            metrics: {
                readySources: Number(
                    position.ready_sources ??
                        summaryStats.ready_sources ??
                        summaryStats.answered ??
                        0,
                ),
                coverage: coverageLabel,
                openMatters: Number(
                    position.open_matters ?? summaryStats.open ?? followUps.length,
                ),
            },
            actions: (position.actions || [{ key: 'save_to_journal', label: 'Save to Journal' }])
                .map(mapAction)
                .filter(Boolean),
        },
        coverageMatrix: matrix,
        followUps,
        matters: followUps,
        filters: {
            priority: filters.priority || 'all',
            scope: filters.scope_area || filters.scope || 'all',
            type: filters.type || 'all',
            impact: filters.impact || 'all',
            status: filters.status || 'all',
            unresolvedOnly: Boolean(filters.unresolved_only ?? true),
            options: {
                priority: filters.options?.priority || ['all', 'critical', 'high', 'medium', 'low'],
                scope: scopeOptionKeys,
                scopeAreaLabels: Object.fromEntries(
                    scopeOptionKeys.map(opt => [
                        opt,
                        opt === 'all' ? 'All' : SCOPE_LABEL(opt),
                    ]),
                ),
                type: filters.options?.type || ['all', 'evidence_gap', 'contradiction', 'claim'],
                impact: filters.options?.impact || ['all'],
                status: filters.options?.status || ['all', 'open', 'in_progress', 'resolved'],
            },
        },
        sourceReadiness: {
            ready: Number(readinessTotals.ready ?? summaryStats.ready_sources ?? 0),
            processing: Number(readinessTotals.processing ?? 0),
            failed: Number(readinessTotals.failed ?? 0),
            failedSourceId: failedSource?.source_id || null,
            failedSourceLabel: failedSource?.label || failedSource?.source_id || null,
            unavailableSources: readiness.unavailable_sources || [],
        },
        newEvidenceResolution: data.new_evidence_resolution || null,
        summary: {
            open: Number(summaryStats.open ?? followUps.length),
            material: Number(summaryStats.material || 0),
            answered: Number(summaryStats.answered || summaryStats.ready_sources || 0),
            unanswered: Number(
                summaryStats.unanswered_estimate ??
                    summaryStats.unanswered ??
                    followUps.length,
            ),
        },
    };
}

function mapEstimate(estimate = {}) {
    const year1 = estimate.year_1 || estimate.year1 || {};
    const year2 = estimate.year_2 || estimate.year2 || {};
    return {
        status: estimate.status || 'not_estimated',
        currency: estimate.currency || 'GBP',
        year1,
        year2,
        year1CapexLabel: estimate.year_1_capex_label || '—',
        year1OpexLabel: estimate.year_1_opex_label || '—',
        year2CapexLabel: estimate.year_2_capex_label || '—',
        year2OpexLabel: estimate.year_2_opex_label || '—',
        confidence: titleCaseToken(estimate.estimate_confidence || estimate.confidence || ''),
        sourceType: estimate.source_type || '',
        sourceLabel: estimate.source_label || 'Not available',
        sourceId: estimate.source_id || null,
        sourceVersion: estimate.source_version || null,
        basisRationale: asDisplayText(estimate.basis_rationale),
        humanReviewed: Boolean(estimate.human_reviewed),
        assumptions: asDisplayTextList(estimate.assumptions),
    };
}

export function remediationInvestmentPayloadToViewModel(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload.data || {};
    const subtitle =
        'Recommended remediation, timing and indicative investment arising from the current analysis.';
    if (data.empty_state && !Array.isArray(data.treatments)) {
        return emptyVm('remediation_investment', 'Remediation & Investment', subtitle, payload);
    }
    const treatments = (data.treatments || []).map(item => {
        const estimate = mapEstimate(item.effective_estimate || {});
        const review = item.latest_review || null;
        return withSupportedActions(
            {
                id: item.treatment_id || item.id,
                treatmentId: item.treatment_id || item.id,
                title: asDisplayText(item.title),
                summary: asDisplayText(item.summary),
                whyRecommended: asDisplayText(item.why_recommended),
                objective: asDisplayText(item.objective),
                assumptions: asDisplayTextList(item.assumptions),
                priority: String(item.priority || 'medium').toLowerCase(),
                priorityLabel: titleCaseToken(item.priority || 'medium'),
                timeHorizon: item.time_horizon || 'year_1',
                timeHorizonLabel: item.time_horizon_label || titleCaseToken(item.time_horizon),
                scopeAreaIds: item.scope_area_ids || [],
                estimate,
                lensOriginal: item.lens_original_estimate || {},
                latestReview: review
                    ? {
                          revision: review.revision,
                          reviewedAt: review.reviewed_at,
                          reviewedByName: review.reviewed_by_name,
                          reviewNote: review.review_note,
                          estimateBasis: review.estimate_basis,
                          confidence: review.confidence,
                      }
                    : null,
                relatedFindings: item.related_findings || [],
                relatedRisks: item.related_risks || [],
                evidenceReferenceIds: item.evidence_reference_ids || [],
                actions: item.actions || [],
            },
            'remediation_investment',
        );
    });
    const summary = data.summary || {};
    return {
        moduleKey: 'remediation_investment',
        title: 'Remediation & Investment',
        subtitle,
        metaLine: formatMetaLine(payload),
        isEmpty: false,
        status: payload.analysis?.status,
        analysisId: payload.analysis?.analysis_id || null,
        currency: summary.currency || 'GBP',
        summary: {
            treatmentCount: Number(summary.treatment_count || treatments.length),
            highCount: Number(summary.high_count || 0),
            unestimatedCount: Number(summary.unestimated_count || 0),
            year1CapexLabel: summary.year_1_capex_label || '—',
            year1OpexLabel: summary.year_1_opex_label || '—',
            year2CapexLabel: summary.year_2_capex_label || '—',
            year2OpexLabel: summary.year_2_opex_label || '—',
            totalsComplete: Boolean(summary.totals_complete),
            label: summary.label || 'Indicative total',
            disclaimer: summary.disclaimer || '',
        },
        navigation: data.navigation?.keys || [
            { key: 'all', label: 'All' },
            { key: 'pre_close', label: 'Pre-close' },
            { key: 'first_100_days', label: '100 Days' },
            { key: 'year_1', label: 'Year 1' },
            { key: 'year_2_plus', label: 'Year 2+' },
        ],
        treatments,
    };
}

const VIEW_MODELS = {
    management_summary: managementSummaryPayloadToViewModel,
    key_findings: keyFindingsPayloadToViewModel,
    swot_analysis: swotPayloadToViewModel,
    investment_opinion: investmentOpinionPayloadToViewModel,
    remediation_investment: remediationInvestmentPayloadToViewModel,
    risks_signals: risksSignalsPayloadToViewModel,
    evidence_follow_ups: evidenceFollowUpsPayloadToViewModel,
};

export function modulePayloadToViewModel(moduleKey, payload) {
    const fn = VIEW_MODELS[moduleKey];
    const vm = fn ? fn(payload) : null;
    if (!vm) return null;
    return {
        ...vm,
        newEvidenceBanner: mapNewEvidenceBanner(payload),
    };
}

export default modulePayloadToViewModel;
